// ─────────────────────────────────────────────────────────────────────────────
// DaemonContext.js — Global state for the desktop daemon connection.
//
// OBSERVE-ONLY. The desktop app owns the VPN; this context watches it and
// nothing more. The connect/disconnect actions that used to live here were
// removed on 2026-07-31 — no UI had ever called them, and a browser-reachable
// "drop this user's tunnel" path is not something to leave lying around. Adding
// tunnel control back means adding it to daemonService too, which is now
// deliberately GET-only.
//
// What this reads from the daemon:
//   GET /health          → is the app running
//   GET /daemon/whoami   → who is signed in
//   GET /vpn/status      → one snapshot on connect
//   WS  /events          → live tunnel state + backend-teardown signal
//
// daemonStatus: "checking" | "offline" | "not-authenticated" | "wrong-user" | "ready"
//   checking          = first probe only; later probes never reset to this
//   offline           = /health did not answer — app closed
//   not-authenticated = daemon reachable but desktop app is not logged in
//   wrong-user        = desktop app is logged in as somebody else
//   ready             = running, signed in, and the same user as this dashboard
// daemonUser:   { userId, email } of the desktop account, or null
// vpnStatus:    VpnStatus from the WebSocket (display only)
// ─────────────────────────────────────────────────────────────────────────────
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  isDaemonRunning,
  getDaemonUser,
  getVpnStatus,
  connectDaemonEvents,
} from "../services/daemonService";
import { useAuth } from "./AuthContext";

const DaemonContext = createContext(null);

export function useDaemon() {
  return useContext(DaemonContext);
}

export function DaemonProvider({ children, onBackendDisconnected }) {
  const { user } = useAuth();
  const [daemonStatus,  setDaemonStatus]  = useState("checking");
  const [daemonUser,    setDaemonUser]    = useState(null);
  const [vpnStatus,     setVpnStatus]     = useState({ state: "disconnected" });

  const stopWsRef = useRef(null);

  // "checking" is a first-load state only. Re-probes run every few seconds
  // forever, and flipping the status back to "checking" each time made the page
  // tear down its panels and rebuild them — a visible flash every interval.
  // After the first settled answer we keep showing it until a *different* one
  // arrives, so a probe that confirms the status quo changes nothing on screen.
  const hasSettled = useRef(false);
  // Mirror of daemonStatus readable inside the async probe without making it a
  // dependency (which would rebuild recheckDaemon on every transition).
  const daemonStatusRef = useRef("checking");

  // ── Health check + WebSocket subscribe ────────────────────────────────────

  const settle = useCallback((status) => {
    hasSettled.current = true;
    daemonStatusRef.current = status;
    // Same value → React bails out of the re-render, so a steady state is free.
    setDaemonStatus(status);
  }, []);

  const recheckDaemon = useCallback(async () => {
    if (!hasSettled.current) setDaemonStatus("checking");

    const alive = await isDaemonRunning();
    if (!alive) {
      settle("offline");
      return;
    }

    // ── Identity check (per daemon-api.md startup workflow, step 2) ──────────
    try {
      const who = await getDaemonUser();
      if (!who.loggedIn) {
        // Desktop app is running but not signed in — block VPN control
        setDaemonUser(null);
        settle("not-authenticated");
        return;
      }

      setDaemonUser((prev) =>
        prev?.userId === who.userId && prev?.email === who.email
          ? prev                              // identical → keep the same object
          : { userId: who.userId, email: who.email });

      // The desktop app holds its own token for its own account. If that is a
      // different person than the dashboard session, every connect/disconnect
      // would act on the wrong account — so surface it instead of proceeding.
      //
      // We do NOT log the dashboard out here: the dashboard session is the
      // valid one, and dropping it would punish the wrong side. The user fixes
      // this in the desktop app.
      if (user?.id && who.userId && who.userId !== user.id) {
        settle("wrong-user");
        return;
      }
    } catch {
      // whoami failed unexpectedly — treat as offline rather than blocking
      setDaemonUser(null);
      settle("offline");
      return;
    }

    // Already ready and still ready — the WebSocket is live and nothing below
    // needs redoing. Bail out before re-subscribing, otherwise every probe
    // would tear the socket down and build a new one.
    if (daemonStatusRef.current === "ready") return;

    settle("ready");

    // Initial snapshot (non-fatal — WS push arrives immediately on connect)
    try {
      const snap = await getVpnStatus();
      setVpnStatus(snap);
    } catch { /* ignore */ }

    // Subscribe WebSocket (tear down any existing connection first)
    if (stopWsRef.current) stopWsRef.current();

    stopWsRef.current = connectDaemonEvents({
      onStatusChanged: (status) => {
        setVpnStatus(status);
      },
      onBackendDisconnected: () => {
        onBackendDisconnected?.();
      },
    });
  }, [onBackendDisconnected, user?.id, settle]);

  useEffect(() => {
    recheckDaemon();
    return () => { if (stopWsRef.current) stopWsRef.current(); };
  }, [recheckDaemon]);

  return (
    <DaemonContext.Provider value={{
      daemonStatus,   // "checking" | "offline" | "not-authenticated" | "wrong-user" | "ready"
      daemonUser,     // { userId, email } of the desktop account | null
      vpnStatus,      // { state, ... } — display only
      recheckDaemon,
    }}>
      {children}
    </DaemonContext.Provider>
  );
}
